import { API_URL } from '@/constants/api';
import type { ShopifyFilters, ShopifyPageInfo, ShopifyResponse } from '@/shared/types/shopify';
import { readJson } from '@/shared/utils/json';
import { toFilterString } from '@/shared/utils/string';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export const useShopifyList = <T, F extends ShopifyFilters>(
  endpoint: string,
  defaultFilters: F,
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pageInfo, setPageInfo] = useState<ShopifyPageInfo>({
    hasNextPage: false,
    hasPreviousPage: false,
    startCursor: null,
    endCursor: null,
    total: 0,
  });

  const getFiltersFromUrl = useCallback(() => {
    const filters = { ...defaultFilters };
    Object.keys(defaultFilters).forEach((key) => {
      const urlValue = searchParams.get(key);
      if (urlValue !== null) {
        (filters as Record<string, unknown>)[key] = urlValue;
      }
    });

    // Add pageSize from URL
    const pageSizeFromUrl = searchParams.get('pageSize');
    if (pageSizeFromUrl) {
      (filters as Record<string, unknown>).pageSize = parseInt(pageSizeFromUrl, 10);
    }

    return filters;
  }, [defaultFilters, searchParams]);

  const [filters, setFilters] = useState<F>(getFiltersFromUrl);

  const fetchItems = useCallback(
    async (cursor?: string, direction: 'after' | 'before' = 'after') => {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();

        Object.keys(defaultFilters).forEach((key) => {
          const urlValue = searchParams.get(key);
          if (urlValue && urlValue.trim() !== '') {
            params.append(key, urlValue);
          }
        });

        // Add pageSize parameter
        const pageSizeFromUrl = searchParams.get('pageSize');
        if (pageSizeFromUrl) {
          params.append('pageSize', pageSizeFromUrl);
        } else {
          params.append('pageSize', '25'); // default from backend
        }

        if (cursor) {
          params.append(direction === 'after' ? 'afterCursor' : 'beforeCursor', cursor);
        }

        const response = await fetch(`${API_URL}${endpoint}?${params}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch ${endpoint}`);
        }

        const data = await readJson<ShopifyResponse<T>>(response);

        const items = data.items || data || [];
        const pageInfo = data.paginationData || {
          hasNextPage: false,
          hasPreviousPage: false,
          startCursor: null,
          endCursor: null,
          total: 0,
        };

        setItems(Array.isArray(items) ? items : []);
        setPageInfo(pageInfo);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [defaultFilters, endpoint, searchParams],
  );

  useEffect(() => {
    void Promise.resolve().then(() => {
      setFilters(getFiltersFromUrl());
      return fetchItems();
    });
  }, [fetchItems, getFiltersFromUrl]);

  const updateUrlParams = useCallback(
    (newFilters: F) => {
      const params = new URLSearchParams();

      Object.entries(newFilters).forEach(([key, value]) => {
        if (value && value !== defaultFilters[key]) {
          params.set(key, String(value));
        }
      });

      setSearchParams(params);
    },
    [defaultFilters, setSearchParams],
  );

  const handleFiltersChange = useCallback(
    (newFilters: Partial<F>) => {
      const updatedFilters = { ...filters, ...newFilters };
      updateUrlParams(updatedFilters);
      // Only reset pagination when filters actually changed. If the user clicks
      // Search again with the same query, the URL stays the same so the effect
      // won't refetch — clearing pageInfo here would hide pagination until refetch.
      const filtersChanged = Object.keys(updatedFilters).some(
        (key) =>
          toFilterString((updatedFilters as Record<string, unknown>)[key]) !==
          toFilterString((filters as Record<string, unknown>)[key]),
      );
      if (filtersChanged) {
        setPageInfo({
          hasNextPage: false,
          hasPreviousPage: false,
          startCursor: null,
          endCursor: null,
        });
      }
    },
    [filters, updateUrlParams],
  );

  const handleClearFilters = useCallback(() => {
    updateUrlParams(defaultFilters);
    setPageInfo({
      hasNextPage: false,
      hasPreviousPage: false,
      startCursor: null,
      endCursor: null,
    });
  }, [defaultFilters, updateUrlParams]);

  const handleNextPage = useCallback(() => {
    if (pageInfo.hasNextPage && pageInfo.endCursor) {
      void fetchItems(pageInfo.endCursor, 'after');
    }
  }, [pageInfo, fetchItems]);

  const handlePreviousPage = useCallback(() => {
    if (pageInfo.hasPreviousPage && pageInfo.startCursor) {
      void fetchItems(pageInfo.startCursor, 'before');
    }
  }, [pageInfo, fetchItems]);

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      const params = new URLSearchParams(searchParams);
      params.set('pageSize', newPageSize.toString());
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const isFiltersEmpty = useCallback(() => {
    return Object.values(filters).every((value) => !value);
  }, [filters]);

  return {
    items,
    loading,
    error,
    pageInfo,
    filters,
    handleNextPage,
    handlePreviousPage,
    handlePageSizeChange,
    handleFiltersChange,
    handleClearFilters,
    isFiltersEmpty,
    refetch: () => fetchItems(),
  };
};
